import React, { useEffect, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import BASE_URL from "../../../config/apiConfig";
import "./RenewFarmPage.css";

const RenewFarmPage = () => {
  const navigate = useNavigate();
  const { fieldId } = useParams();
  const { state } = useLocation();
  const planStatus = state?.status || "created";

  const [fieldData, setFieldData] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState("month");
  const [totalPrice, setTotalPrice] = useState(null);
  const [spinnerLoading, setSpinnerLoading] = useState(false);

  const [priceBreakdown, setPriceBreakdown] = useState({
    basePrice: 0,
    gst: 0,
    total: 0,
  });
  const planOptions = [
    {
      value: "month",
      label: "Monthly",
      frequency: 1,
      discountText: "No discount",
    },
    {
      value: "quarter",
      label: "Quarterly",
      frequency: 3,
      discountText: "10% discount",
    },
    {
      value: "year",
      label: "Annual",
      frequency: 12,
      discountText: "20% discount",
    },
  ];

  useEffect(() => {
    const fetchFieldData = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/api/user/farm-management/fetch-farm-data-from-db/${fieldId}`
        );
        const data = await response.json();
        setFieldData(data);
      } catch (err) {
        console.error("Error fetching field data", err);
      }
    };

    fetchFieldData();
  }, [fieldId]);

  const handlePayNow = async () => {
    if (!fieldData) return;

    setSpinnerLoading(true);

    const selectedPlanOption = planOptions.find(
      (p) => p.value === selectedPlan
    );
    const amountInPaise = Math.round(Number(totalPrice) * 100);

    try {
      // Step 1: Create Razorpay Order
      const orderRes = await fetch(`${BASE_URL}/api/payment/order-renewal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: amountInPaise,
          currency: "INR",
          field_id: fieldId,
          email: fieldData.email,
          planDetails: {
            crop_name: fieldData.crop_code?.name,
            field_area: fieldData.field_area,
            plan_name: fieldData.plan_name,
            payment_frequency: selectedPlanOption.frequency,
          },
        }),
      });

      const order = await orderRes.json();

      if (!order.id) {
        alert("Failed to initiate payment.");
        setSpinnerLoading(false);
        return;
      }

      // Step 2: Open Razorpay
      const options = {
        key: "rzp_test_gHih3yGs0quJo5",
        amount: amountInPaise,
        currency: "INR",
        name: "Farmology India",
        description: `Renewal for ${fieldData.field_name}`,
        image:
          "https://farmologyindia.com/wp-content/uploads/elementor/thumbs/cropped-frmlgy-logo-qr7lic6cxgrollie5a58ze7z8a35cxz8b3zso6egs4.png",
        order_id: order.id,

        handler: async (response) => {
          if (
            !response.razorpay_payment_id ||
            !response.razorpay_order_id ||
            !response.razorpay_signature
          ) {
            alert("Invalid payment response from Razorpay.");
            setSpinnerLoading(false);
            return;
          }

          // Step 3: Validate Payment
          const payload = {
            razorpay_order_id: order.id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            field_id: fieldId,
            email: fieldData.email,
            payment_frequency: selectedPlanOption.frequency,
          };

          const validateRes = await fetch(
            `${BASE_URL}/api/payment/validate-renewal`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(payload),
            }
          );

          const result = await validateRes.json();

          if (result.success) {
            try {
              alert("Renewal successful!");

              navigate("/user/crop-health-management");
            } catch (error) {
              console.error("❌ Error in addMonthsToFarm:", error);
              alert(
                "Renewal succeeded, but field update failed. Please contact support."
              );
            } finally {
              setSpinnerLoading(false);
            }
          } else {
            alert("Payment validation failed");
            setSpinnerLoading(false);
          }
        },

        prefill: {
          email: fieldData.email,
        },
        modal: {
          ondismiss: () => {
            // Close spinner if payment window is closed
            setSpinnerLoading(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      console.error("Payment error:", err);
      alert("Payment failed. Please try again.");
      setSpinnerLoading(false);
    }
  };

  useEffect(() => {
    if (fieldData?.field_area && fieldData?.per_hectare_price) {
      calculateTotalPrice(fieldData.field_area, fieldData.per_hectare_price);
    }
  }, [fieldData, selectedPlan]);

  const calculateTotalPrice = async (landSize, perHectarePrice) => {
    const selectedPlanOption = planOptions.find(
      (p) => p.value === selectedPlan
    );
    if (!selectedPlanOption) return;

    try {
      console.log("🔍 Calculating price for:");
      console.log("Land Size:", landSize);
      console.log("Per Hectare Price:", perHectarePrice);
      console.log(
        "Selected Plan Frequency (months):",
        selectedPlanOption.frequency
      );

      const response = await fetch(`${BASE_URL}/api/calculate-price`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          landSize,
          perHectarePrice,
        }),
      });

      if (!response.ok) throw new Error("Failed to calculate price");

      const data = await response.json();
      let finalPrice = data.calculatedPrice;

      console.log("🧾 Raw Calculated Price (before discount):", finalPrice);

      // Apply plan-based discount
      if (selectedPlanOption.frequency === 3) {
        finalPrice *= 3 * 0.9;
        console.log("📉 Applied quarterly discount (10%)");
      }
      if (selectedPlanOption.frequency === 12) {
        finalPrice *= 12 * 0.8;
        console.log("📉 Applied annual discount (20%)");
      }

      const total = parseFloat(finalPrice.toFixed(2));
      const base = parseFloat((total / 1.18).toFixed(2));
      const gst = parseFloat((total - base).toFixed(2));

      console.log("✅ Final Total Price:", total);
      console.log("💰 Base Price:", base);
      console.log("🧾 GST (18%):", gst);

      setTotalPrice(total.toFixed(2));
      setPriceBreakdown({ basePrice: base, gst, total });
    } catch (err) {
      console.error("❌ Calculation failed:", err);
    }
  };

  if (!fieldData)
    return <div className="loading">Loading field details...</div>;

  return (
    <div className="farm-checkout-page">
      {spinnerLoading && (
        <div className="loader-overlay">
          <div className="loader-content">
            <div className="spinner" />
            <h2>Processing Payment...</h2>
          </div>
        </div>
      )}

      <div className="checkout-card">
        <h2>{fieldData.field_name || "Unnamed Field"}</h2>
        <p className="field-id">
          Field ID: <strong>{fieldData.field_id}</strong>
        </p>

        <div className="details-grid">
          <div>
            <strong>Area:</strong> {fieldData.field_area} ha
          </div>
          <div>
            <strong>
              {fieldData.crop_code?.name?.toLowerCase() === "tea"
                ? "Pruning Date:"
                : "Sowing Date:"}
            </strong>{" "}
            {fieldData.sowing_date || "N/A"}
          </div>

          <div>
            <strong>Latitude:</strong> {fieldData.latitude}
          </div>
          <div>
            <strong>Longitude:</strong> {fieldData.longitude}
          </div>
          <div>
            <strong>Plan Status:</strong> {fieldData.plan_status}
          </div>
          <div>
            <strong>Expires At:</strong> {fieldData.expires_at || "N/A"}
          </div>
          <div>
            <strong>Email:</strong> {fieldData.email || "N/A"}
          </div>
          <div>
            <strong>Crop Code:</strong> {fieldData.crop_code?.name || "N/A"}
          </div>
          <div>
            <strong>Per Hectare Price:</strong> ₹{fieldData.per_hectare_price}
          </div>
        </div>

        <div className="plan-options">
          {planOptions.map((plan) => (
            <label key={plan.value} className="plan-option-label">
              <input
                type="radio"
                name="plan"
                value={plan.value}
                checked={selectedPlan === plan.value}
                onChange={() => setSelectedPlan(plan.value)}
              />
              <span>
                {plan.label} ({plan.discountText})
              </span>
            </label>
          ))}
        </div>

        <div className="price-breakdown">
          <h3>Price Breakdown</h3>
          <div className="price-row">
            <span>Base Price:</span>
            <span>₹{priceBreakdown.basePrice.toFixed(2)}</span>
          </div>
          <div className="price-row">
            <span>GST (18%):</span>
            <span>₹{priceBreakdown.gst.toFixed(2)}</span>
          </div>
          <div className="price-row total">
            <span>Total Payable:</span>
            <span>₹{priceBreakdown.total.toFixed(2)}</span>
          </div>
        </div>

        <button className="pay-now" onClick={handlePayNow}>
          Pay Now
        </button>
      </div>
    </div>
  );
};

export default RenewFarmPage;
